/**
 * AVA Design System — LogoMark
 *
 * Isotipo de AVA (la "A" stylized + el punto cyan). Versión React del logo
 * con prop `tone` para alternar entre duotone (light backgrounds) y all-white
 * (dark backgrounds como el hero gradient).
 *
 * SVG path coords vienen de .design-source/assets/logo-ava-mark.svg (canonical).
 *
 * Usar este componente en lugar de:
 *   - <Shield />, <ShieldCheck /> como placeholder de logo
 *   - <img src="/assets/logo-ava-mark.svg" /> cuando se necesita cambiar color
 *
 * El archivo estático en /assets/logo-ava-mark.svg sigue disponible para
 * casos donde no se renderiza React (favicon, og:image, emails).
 */

interface LogoMarkProps {
  /** Variante de color. "ink" (default) para fondos claros, "white" para fondos oscuros. */
  tone?: "ink" | "white";
  /** Tamaño en px del lado mayor. Default 28. */
  size?: number;
  className?: string;
  /** aria-label alternativo. Default "AVA". */
  ariaLabel?: string;
}

export function LogoMark({
  tone = "ink",
  size = 28,
  className,
  ariaLabel = "AVA",
}: LogoMarkProps) {
  const ink = tone === "white" ? "#ffffff" : "#4D5154";
  const cyan = tone === "white" ? "#ffffff" : "#05A0D9";

  return (
    <svg
      viewBox="0 0 342 321"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={ariaLabel}
    >
      <polygon
        fill={ink}
        points="237,191 213,157 104,220 152,135 125,95 10,295 61,295"
      />
      <polygon
        fill={cyan}
        points="122,21 237,190 275,168 176,21"
      />
      <circle fill={cyan} cx="280" cy="252" r="43" />
    </svg>
  );
}

/**
 * Wordmark completo "AVA" — texto + isotipo en línea.
 * Para tipografía del wordmark usa Inter weight 700 (consistente con .ava-display).
 */
interface LogoProps {
  tone?: "ink" | "white";
  size?: number;
  className?: string;
}

export function Logo({ tone = "ink", size = 28, className }: LogoProps) {
  const textColor = tone === "white" ? "text-white" : "text-foreground";
  return (
    <span className={`inline-flex items-center gap-2 ${className ?? ""}`}>
      <LogoMark tone={tone} size={size} />
      <span className={`font-bold tracking-tight ${textColor}`} style={{ fontSize: size * 0.7, fontFamily: "var(--ava-font-sans)" }}>
        AVA
      </span>
    </span>
  );
}
